// const API_BASE_URL = 'http://localhost:5000/api';
const API_BASE_URL = 'https://backend-deeps-projects-1a2428cf.vercel.app/api';

// Helper function to get auth token
const getAuthToken = () => {
  const authData = localStorage.getItem('shiv_admin_auth');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      return parsed.access_token || parsed.token;
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Helper function to make authenticated requests
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options,
  };
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  
  // Handle authentication errors
  if (response.status === 401) {
    localStorage.removeItem('shiv_admin_auth');
    window.location.href = '/';
    throw new Error('Authentication required');
  }
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }
  
  return data;
};

// Helper function for form data requests (file uploads)
const apiRequestFormData = async (endpoint, formData) => {
  const token = getAuthToken();
  
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData,
  });
  
  if (response.status === 401) {
    localStorage.removeItem('shiv_admin_auth');
    window.location.href = '/';
    throw new Error('Authentication required');
  }
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }
  
  return data;
};

// Authentication API
export const authAPI = {
  login: async (username, password) => {
    return apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  
  verify: async () => {
    return apiRequest('/auth/verify');
  },
  
  logout: async () => {
    return apiRequest('/auth/logout', {
      method: 'POST',
    });
  },
  
  getProfile: async () => {
    return apiRequest('/auth/profile');
  },
};

// Collections API
export const collectionsAPI = {
  getAll: async () => {
    return apiRequest('/collections');
  },
  
  getById: async (id) => {
    return apiRequest(`/collections/${id}`);
  },
  
  create: async (collectionData) => {
    return apiRequest('/collections', {
      method: 'POST',
      body: JSON.stringify(collectionData),
    });
  },
  
  update: async (id, collectionData) => {
    return apiRequest(`/collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(collectionData),
    });
  },
  
  delete: async (id) => {
    return apiRequest(`/collections/${id}`, {
      method: 'DELETE',
    });
  },
  
  recalculateCount: async (id) => {
    return apiRequest(`/collections/${id}/recalculate-count`, {
      method: 'POST',
    });
  },
};

// Products API
export const productsAPI = {
  getAll: async (collectionId = null, search = null) => {
    let url = '/products';
    const params = new URLSearchParams();
    
    if (collectionId) {
      params.append('collection_id', collectionId);
    }
    if (search) {
      params.append('search', search);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    return apiRequest(url);
  },
  
  getById: async (id) => {
    return apiRequest(`/products/${id}`);
  },
  
  getByCollection: async (collectionId) => {
    return apiRequest(`/products/collection/${collectionId}`);
  },
  
  create: async (productData) => {
    return apiRequest('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  },
  
  update: async (id, productData) => {
    return apiRequest(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  },
  
  delete: async (id) => {
    return apiRequest(`/products/${id}`, {
      method: 'DELETE',
    });
  },
  
  updateStock: async (id, size, quantity) => {
    return apiRequest(`/products/${id}/stock`, {
      method: 'PUT',
      body: JSON.stringify({ size, quantity }),
    });
  },
  
  search: async (query, collectionId = null) => {
    let url = `/products/search?q=${encodeURIComponent(query)}`;
    if (collectionId) {
      url += `&collection_id=${collectionId}`;
    }
    return apiRequest(url);
  },
  
  getStats: async () => {
    return apiRequest('/products/stats');
  },
};

// Upload API
export const uploadAPI = {
  uploadImage: async (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return apiRequestFormData('/upload/image', formData);
  },
  
  uploadImages: async (imageFiles) => {
    const formData = new FormData();
    imageFiles.forEach((file) => {
      formData.append('images', file);
    });
    return apiRequestFormData('/upload/images', formData);
  },
  
  uploadImageBase64: async (base64Data) => {
    return apiRequest('/upload/image', {
      method: 'POST',
      body: JSON.stringify({ image: base64Data }),
    });
  },
  
  uploadImagesBase64: async (base64Array) => {
    return apiRequest('/upload/images', {
      method: 'POST',
      body: JSON.stringify({ images: base64Array }),
    });
  },
  
  deleteImage: async (publicId) => {
    return apiRequest(`/upload/image/${publicId}`, {
      method: 'DELETE',
    });
  },
  
  cleanupImages: async () => {
    return apiRequest('/upload/images/cleanup', {
      method: 'POST',
    });
  },
};

// Health check API
export const healthAPI = {
  check: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      return response.json();
    } catch (error) {
      throw new Error('Backend server is not running');
    }
  },
};

// Orders API (Admin)
export const ordersAPI = {
  getAdminOrders: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    const query = searchParams.toString();
    const url = `/orders/admin${query ? `?${query}` : ''}`;
    return apiRequest(url);
  },

  getAdminOrderDetail: async (orderId) => {
    return apiRequest(`/orders/admin/${orderId}`);
  },

  updateOrderStatus: async (orderId, status) => {
    return apiRequest(`/orders/admin/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  updateOrder: async (orderId, updates) => {
    return apiRequest(`/orders/admin/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  createShipment: async (orderId) => {
    return apiRequest(`/orders/admin/${orderId}/shipment`, {
      method: 'POST',
    });
  },

  cancelOrder: async (orderId, reason = 'Cancelled by admin') => {
    return apiRequest(`/orders/admin/${orderId}/cancel`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  },

  trackOrder: async (orderId) => {
    return apiRequest(`/orders/${orderId}/tracking`);
  },
};

// Landing page management API (Admin)
export const adminAPI = {
  getLandingPageSettings: async () => {
    return apiRequest('/landing-page/settings');
  },

  uploadBackgroundImage: async (formData) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/landing-page/upload-background`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    
    if (response.status === 401) {
      localStorage.removeItem('shiv_admin_auth');
      window.location.href = '/';
      throw new Error('Authentication required');
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }
    
    return { data };
  },

  uploadStoryImage: async (formData) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/landing-page/upload-story`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.status === 401) {
      localStorage.removeItem('shiv_admin_auth');
      window.location.href = '/';
      throw new Error('Authentication required');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    return { data };
  },

  updateLandingPageSettings: async (settingsData) => {
    return apiRequest('/landing-page/update-settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  },

  resetLandingPageSettings: async () => {
    return apiRequest('/landing-page/reset-to-defaults', {
      method: 'POST',
    });
  },

  // Clear all store data except products and collections
  resetStoreData: async () => {
    return apiRequest('/admin/reset-data', {
      method: 'POST',
    });
  },
};

// Coupons API (Admin)
export const couponsAPI = {
  getAll: async () => {
    return apiRequest('/coupons');
  },

  getById: async (id) => {
    return apiRequest(`/coupons/${id}`);
  },

  create: async (couponData) => {
    return apiRequest('/coupons', {
      method: 'POST',
      body: JSON.stringify(couponData),
    });
  },

  update: async (id, couponData) => {
    return apiRequest(`/coupons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(couponData),
    });
  },

  delete: async (id) => {
    return apiRequest(`/coupons/${id}`, {
      method: 'DELETE',
    });
  },
};

// Subscribers API (Admin)
export const subscribersAPI = {
  getAll: async () => {
    return apiRequest('/subscribers');
  },

  delete: async (id) => {
    return apiRequest(`/subscribers/${id}`, {
      method: 'DELETE',
    });
  },
};

// Analytics API (Admin)
export const analyticsAPI = {
  getOverview: async (days = 30) => {
    return apiRequest(`/analytics/overview?days=${days}`);
  },

  getSales: async (days = 30) => {
    return apiRequest(`/analytics/sales?days=${days}`);
  },

  getProducts: async (days = 30) => {
    return apiRequest(`/analytics/products?days=${days}`);
  },

  getInventory: async () => {
    return apiRequest('/analytics/inventory');
  },

  getCustomers: async (days = 30) => {
    return apiRequest(`/analytics/customers?days=${days}`);
  },

  getProductDetail: async (productId, days = 90) => {
    return apiRequest(`/analytics/product/${productId}?days=${days}`);
  },
};

const api = {
  authAPI,
  collectionsAPI,
  productsAPI,
  uploadAPI,
  healthAPI,
  ordersAPI,
  adminAPI,
  analyticsAPI,
  couponsAPI,
  subscribersAPI,
};

export default api; 